import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, Drawer } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CloseIcon from '@mui/icons-material/Close';
import flowerIcon from '../img/flowers.svg';

// Zoom profond par tuiles DZI (generees par libvips dzsave, voir docs/TUILES.md).
// Le visualiseur ne charge que les tuiles visibles : les originaux, de 158 Mo a
// 3,7 Go, ne sont jamais telecharges en entier.
//
// Les tuiles sont servies par le stockage objet (Garage, VPS studio) quand
// REACT_APP_TILES_BASE est defini ; sinon depuis public/, pour developper sans
// reseau.
const TILES_BASE = (process.env.REACT_APP_TILES_BASE || '').replace(/\/$/, '');
const tileUrl = (name) =>
  TILES_BASE ? `${TILES_BASE}/dzi/${name}` : `/assets/frescoes/dzi/${name}`;

const FRESCOES = {
  littlemermaid: {
    title: 'The Life and Death of the Little Mermaid',
    // Version upscalee x2 : 78662 x 11812 (929 Mpx).
    source: 'mermaid-hd.dzi',
    // Version d'origine, pour comparer via ?sd
    fallbackSource: 'mermaid.dzi',
    // L'upscale a double les pixels : sans ce plafond, le zoom irait deux fois
    // au-dela de la resolution reelle de l'oeuvre, dans du detail invente.
    upscaleFactor: 2,
    paragraphs: [
      'The Life and Death of the Little Mermaid is a piece from 2019, created in one continuous flow on a 1.5 x 10 meter sheet of paper, all in ink and without any preliminary sketches. Kajika’s art speaks of inner monsters but also of animal and human-like creatures that help reveal and heal from trauma.',
    ],
  },
  imieiamori: {
    title: 'Imieiamori',
    source: 'imieiamori.dzi',
    paragraphs: [
      'Imieiamori is an artwork from 2020, created entirely on a single 1.5×10m sheet of paper, with no initial sketches, using just Indian ink and watercolor. It’s a journey and a story rolled into one. Imieiamori tells the tale of the Octopus Sun, who, almost by accident, brought millions of lives into being through its tentacles.',
      'With Imieiamori, Kajika creates a powerful narrative that strikingly blends the contrasts of destruction and renewal, death and rebirth, darkness and an overwhelming richness of color.',
      'It is also the only work of art to contain the first appearance of Flowers of Joy. This piece reveals the intensity of the intimate quest to bring the unique Flower of Joy into bloom.',
    ],
  },
};

const FrescoZoom = ({ fresco }) => {
  const params = useParams();
  const key = fresco || params.fresco;
  const artwork = FRESCOES[key];

  const viewerRef = useRef(null);
  const osdRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  // ?sd bascule sur la source d'origine quand une version upscalee existe.
  const useFallback =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('sd');

  useEffect(() => {
    if (!artwork) return undefined;

    const OpenSeadragon = window.OpenSeadragon;
    if (!OpenSeadragon) {
      setFailed('OpenSeadragon n’est pas charge.');
      return undefined;
    }

    const file =
      useFallback && artwork.fallbackSource ? artwork.fallbackSource : artwork.source;

    const viewer = OpenSeadragon({
      element: viewerRef.current,
      tileSources: tileUrl(file),
      prefixUrl: 'https://openseadragon.github.io/openseadragon/images/',
      showNavigationControl: false,
      // Les fresques font 6,7x plus large que haut : on les affiche entieres au
      // depart, puis le zoom va jusqu'au pixel d'origine.
      defaultZoomLevel: 0,
      // 0.2 = on peut s'eloigner jusqu'a voir la fresque a un cinquieme de la
      // largeur d'ecran, avec de la marge autour.
      minZoomImageRatio: 0.2,
      // Le zoom s'arrete a la resolution reelle de l'oeuvre : sur une fresque
      // upscalee, on divise par le facteur pour ne pas entrer dans les pixels
      // ajoutes par l'agrandissement.
      maxZoomPixelRatio:
        1 / (useFallback && artwork.fallbackSource ? 1 : artwork.upscaleFactor || 1),
      // visibilityRatio a 1 forcerait la fresque a toujours remplir l'ecran, ce
      // qui annulerait le dezoom autorise ci-dessus.
      visibilityRatio: 0.3,
      constrainDuringPan: false,
      animationTime: 0.8,
      springStiffness: 6.5,
      gestureSettingsTouch: { pinchToZoom: true, flickEnabled: true },
      showNavigator: false,
    });

    viewer.addHandler('open', () => setReady(true));
    viewer.addHandler('open-failed', (event) =>
      setFailed(event.message || 'Chargement des tuiles impossible.')
    );

    osdRef.current = viewer;
    return () => {
      viewer.destroy();
      osdRef.current = null;
    };
  }, [artwork, useFallback]);

  const zoomBy = (factor) => {
    const viewer = osdRef.current;
    if (!viewer) return;
    viewer.viewport.zoomBy(factor);
    viewer.viewport.applyConstraints();
  };

  const resetView = () => {
    const viewer = osdRef.current;
    if (viewer) viewer.viewport.goHome();
  };

  const buttonSx = {
    color: '#000000',
    borderRadius: '50%',
    backgroundColor: '#FFF',
    width: '44px',
    height: '44px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
    '&:hover': { backgroundColor: '#ffffff' },
  };

  if (!artwork) {
    return <div>Artwork not found</div>;
  }

  return (
    <>
      <Helmet>
        <title>{`KAJIKA AKI - ${artwork.title}`}</title>
      </Helmet>

      <Box sx={{ position: 'fixed', inset: 0, backgroundColor: '#f5f5f5' }}>
        <Box ref={viewerRef} sx={{ position: 'absolute', inset: 0 }} />

        {!ready && !failed && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src={flowerIcon}
              alt="Loading"
              sx={{ width: '45px', height: '45px', animation: 'spin 6s linear infinite' }}
            />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </Box>
        )}

        {failed && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              px: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ mb: 1 }}>
              Zoom indisponible
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              {failed}
            </Typography>
          </Box>
        )}

        <IconButton
          onClick={() => navigate('/')}
          aria-label="Retour"
          sx={{ ...buttonSx, position: 'fixed', top: 10, left: 10, zIndex: 10 }}
        >
          <ArrowBackOutlinedIcon sx={{ fontSize: '22px' }} />
        </IconButton>

        <IconButton
          onClick={() => setDrawerOpen(true)}
          aria-label="Informations"
          sx={{ ...buttonSx, position: 'fixed', top: 10, right: 10, zIndex: 10 }}
        >
          <BookmarkIcon sx={{ fontSize: '22px' }} />
        </IconButton>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: { width: { xs: '100%', sm: '400px' }, backgroundColor: '#fff', padding: 2 },
          }}
          transitionDuration={300}
        >
          <Box sx={{ position: 'relative', height: '100%' }}>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              aria-label="Fermer"
              sx={{ position: 'absolute', top: 10, left: 20, color: 'black' }}
            >
              <CloseIcon />
            </IconButton>
            <Box sx={{ padding: 4, marginTop: 5 }}>
              <Typography variant="h6" gutterBottom>
                {artwork.title}
              </Typography>
              {artwork.paragraphs.map((text, index) => (
                <Typography
                  key={text.slice(0, 24)}
                  variant="body1"
                  sx={index > 0 ? { marginTop: 2 } : undefined}
                >
                  {text}
                </Typography>
              ))}
            </Box>
          </Box>
        </Drawer>

        <Box
          sx={{
            position: 'fixed',
            bottom: 15,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 1,
            zIndex: 10,
          }}
        >
          <IconButton onClick={() => zoomBy(0.7)} aria-label="Dezoomer" sx={buttonSx}>
            <RemoveIcon />
          </IconButton>
          <IconButton onClick={resetView} aria-label="Vue entiere" sx={buttonSx}>
            <HomeOutlinedIcon />
          </IconButton>
          <IconButton onClick={() => zoomBy(1.4)} aria-label="Zoomer" sx={buttonSx}>
            <AddIcon />
          </IconButton>
        </Box>
      </Box>
    </>
  );
};

export default FrescoZoom;
